import {Stack} from '@customink/pigment-react';
import {useMobileBreakpoint} from '@customink/pigment-react/lib/utils/useBreakpoint';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo} from 'react';
import ProductFeatureBadges from './components/Badges/ProductFeatureBadges';
import ProductPromoBadges from './components/Badges/ProductPromoBadges';
import ProductCardColorSwatches from './components/ProductCardColorSwatches';
import ProductCardContainer from './components/ProductCardContainer';
import ProductCardDetailsContainer from './components/ProductCardDetailsContainer';
import ProductCardImage from './components/ProductCardImage';
import ProductCardImageContainer from './components/ProductCardImageContainer';
import ProductCardTitle from './components/ProductCardTitle';
import useProductCardLink from './hooks/useProductCardLink';

export default function ComingSoonProductCard({
  badges,
  containerProps = {},
  displayAllSwatches,
  displayImageAsCover = false,
  getProductImageSrcData,
  initialSelectedColorId,
  minQtyToggleSet,
  name,
  onSelectProduct,
  productColors,
  productId,
  productPath
}) {
  const isMobile = useMobileBreakpoint();
  const imageData = useMemo(
    () => getProductImageSrcData(),
    [getProductImageSrcData]
  );

  const linkHref = useProductCardLink({
    badges,
    initialSelectedColorId,
    minQtyToggleSet,
    productPath,
    selectedColorId: undefined
  });

  const onProductCardClick = useCallback(() => {
    onSelectProduct?.();
    window.location.href = linkHref;
  }, [linkHref, onSelectProduct]);

  // Memoize the swatches component to prevent unnecessary re-renders
  const swatchesComponent = useMemo(
    () => (
      <Stack direction="row" alignItems="flex-start">
        <ProductCardColorSwatches
          productColors={productColors}
          displayAllSwatches={displayAllSwatches}
        />
      </Stack>
    ),
    [productColors, displayAllSwatches]
  );

  return (
    <ProductCardContainer
      isMobile={isMobile}
      data-testid="product-preview"
      data-style-id={productId}
      onClick={onProductCardClick}
      position="relative"
      {...containerProps}>
      <ProductCardImageContainer href={linkHref}>
        <ProductCardImage
          cover={displayImageAsCover}
          src={imageData.src}
          srcSet={imageData.srcset}
          alt={name}
          loading="lazy"
          itemProp="image"
          id={`product-image-${productId}`}
        />
        {/* Badges */}
        <ProductPromoBadges badges={badges} compact={isMobile} />
        <ProductFeatureBadges
          badges={badges}
          compact={isMobile}
          productName={name}
        />
      </ProductCardImageContainer>
      <ProductCardDetailsContainer isMobile={isMobile}>
        {/* Conditionally render components based on viewport */}
        {isMobile && (
          <ProductCardTitle
            compact
            data-testid="product-title"
            aria-label={`View details for ${name}`}
            href={linkHref}
            itemProp="name">
            {name}
          </ProductCardTitle>
        )}

        {/* Limited number of swatches */}
        {!displayAllSwatches && swatchesComponent}

        {/* Product Title - desktop only (after swatches) */}
        {!isMobile && (
          <ProductCardTitle
            data-testid="product-title"
            aria-label={`View details for ${name}`}
            href={linkHref}
            itemProp="name">
            {name}
          </ProductCardTitle>
        )}
        {displayAllSwatches && swatchesComponent}
      </ProductCardDetailsContainer>
    </ProductCardContainer>
  );
}

ComingSoonProductCard.propTypes = {
  badges: PropTypes.array,
  containerProps: PropTypes.object,
  displayAllSwatches: PropTypes.bool.isRequired,
  getProductImageSrcData: PropTypes.func.isRequired,
  initialSelectedColorId: PropTypes.number.isRequired,
  minQtyToggleSet: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onSelectProduct: PropTypes.func,
  productColors: PropTypes.array.isRequired,
  productId: PropTypes.number.isRequired,
  productPath: PropTypes.string.isRequired
};
